import { ChangeEvent, FunctionComponent, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Stack,
    TextField,
    Typography,
    TypographyProps,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CashDeskWithCredentials, CreateCashDeskBody } from '@/models/cashDesk';
import { useGetAllLiftOperatorsQuery } from '@/api/v2/liftOperator';
import { LiftOperator } from '@/models/liftoperator';
import { usePostCashDeskMutation } from '@/api/v2/cashDesk';
import { useNotification } from '@starjack/features/notifications';

const typographyStyling: TypographyProps['sx'] = {
    width: '100%',
};

type CashDeskDrawerContentProps = {
    onClose: () => void;
};

export const CashDeskDrawerContent: FunctionComponent<
    CashDeskDrawerContentProps
> = ({ onClose }) => {
    const { data: liftOperators } = useGetAllLiftOperatorsQuery();
    const [postCashDesk, { isLoading }] = usePostCashDeskMutation();
    const [localCashDesk, setLocalCashDesk] = useState<CreateCashDeskBody>({
        username: '',
        password: '',
        liftOperatorId: '',
    });
    const { addNotification } = useNotification();

    const onInputChange =
        (key: keyof CashDeskWithCredentials) =>
        (e: ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;

            setLocalCashDesk((prevState) => ({
                ...prevState,
                [key]: value,
            }));
        };

    const onSaveCashDesk = () => {
        postCashDesk(localCashDesk)
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Kasse erfolgreich angelegt',
                    type: 'success',
                });
                onClose();
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Anlegen. Bitte später noch einmal versuchen',
                });
            });
    };

    if (!localCashDesk) return null;

    return (
        <Box sx={{ width: '440px' }}>
            <Box>
                <Typography
                    variant='h6'
                    sx={{
                        bgcolor: 'primary.dark',
                        color: '#fff',
                        p: 4,
                    }}
                >
                    Neue Kasse anlegen
                </Typography>
            </Box>

            <Stack
                sx={{
                    p: 4,
                }}
                spacing={3}
            >
                <Autocomplete
                    options={getSortedLiftOperators(liftOperators)}
                    getOptionLabel={(option: LiftOperator) => option.name.de}
                    getOptionKey={(option: LiftOperator) => option.id}
                    slotProps={{
                        paper: {
                            sx: {
                                mt: 1,
                                boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.32)',
                            },
                        },
                    }}
                    value={
                        liftOperators?.find(
                            (op) => op.id === localCashDesk.liftOperatorId
                        ) || null
                    }
                    onChange={(_, value) => {
                        setLocalCashDesk((prevWebshop) => ({
                            ...prevWebshop,
                            liftOperatorId: value?.id || '',
                        }));
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label='Bergbahn auswählen'
                            sx={{ ...typographyStyling }}
                        />
                    )}
                />

                <TextField
                    sx={{ ...typographyStyling }}
                    onChange={onInputChange('username')}
                    value={localCashDesk.username}
                    label='Name'
                    required
                />

                <TextField
                    sx={{ ...typographyStyling }}
                    type={'password'}
                    onChange={onInputChange('password')}
                    value={localCashDesk.password}
                    label='Passwort'
                    required
                />

                <LoadingButton
                    sx={{ ...typographyStyling }}
                    variant='contained'
                    onClick={onSaveCashDesk}
                    disabled={
                        !localCashDesk.username ||
                        !localCashDesk.password ||
                        !localCashDesk.liftOperatorId
                    }
                    loading={isLoading}
                >
                    Speichern
                </LoadingButton>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <Button
                        sx={{
                            width: 200,
                            mx: 'auto',
                        }}
                        onClick={onClose}
                        variant='outlined'
                    >
                        Abbrechen
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
};

const getSortedLiftOperators = (liftOperators?: LiftOperator[]) => {
    return (
        liftOperators
            ?.slice()
            .sort((a, b) => a.name.de.localeCompare(b.name.de)) || []
    );
};
